import React, { useState, useEffect } from 'react';
import metadataService from '../../services/metadataService';
import SEO from '../../Components/Common/SEO';
import {
    MapPin,
    Phone,
    Mail,
    Clock,
    Send,
    MessageSquare,
    User,
    Building,
    CheckCircle,
    AlertCircle,
    Facebook,
    Twitter,
    Instagram,
    Linkedin,
    Youtube
} from 'lucide-react';

const Contact = () => {
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        subject: '',
        category: '',
        message: '',
        agreeToTerms: false
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        // Simulate submission
        try {
            await new Promise(resolve => setTimeout(resolve, 2000));
            setSubmitStatus('success');
            setFormData({
                firstName: '',
                lastName: '',
                email: '',
                phone: '',
                subject: '',
                category: '',
                message: '',
                agreeToTerms: false
            });
        } catch (error) {
            setSubmitStatus('error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const contactInfo = [
        {
            icon: Phone,
            title: 'Phone Numbers',
            details: ['+254 712 292 899'],
            description: 'Call us Monday to Friday, 8AM - 6PM'
        },
        {
            icon: Mail,
            title: 'Email Address',
            details: ['itorama254@gmail.com'],
            description: 'We respond within 24 hours'
        },
        {
            icon: MapPin,
            title: 'Office Location',
            details: ['Westlands Square, 4th Floor', 'Ring Road, Westlands, Nairobi'],
            description: 'Visit us Monday to Friday, 9AM - 5PM'
        },
        {
            icon: Clock,
            title: 'Business Hours',
            details: ['Monday - Friday: 8AM - 6PM', 'Saturday: 9AM - 2PM'],
            description: 'Sunday: Closed'
        }
    ];

    const officeLocations = [
        {
            name: 'Nairobi Head Office',
            address: 'Westlands Square, 4th Floor, Ring Road, Westlands',
            phone: '+254 712 292 899',
            email: 'itorama254@gmail.com',
            hours: 'Mon-Fri: 8AM-6PM, Sat: 9AM-2PM'
        },
       /* {
            name: 'Mombasa Branch',
            address: 'Nyali Centre, 2nd Floor, Links Road, Nyali',
            phone: '+254 700 234 567',
            email: 'mombasa@rentutopia.co.ke',
            hours: 'Mon-Fri: 8AM-5PM, Sat: 9AM-1PM'
        },
        {
            name: 'Kisumu Branch',
            address: 'Mega Plaza, 3rd Floor, Oginga Odinga Street',
            phone: '+254 700 345 678',
            email: 'kisumu@rentutopia.co.ke',
            hours: 'Mon-Fri: 8AM-5PM'
        }*/
    ];

    const defaultFaqItems = [
        {
            question: 'How do I list my property?',
            answer: 'You can list your property by creating a property manager account and following our simple listing process. Our team will verify and publish your listing within 24 hours.'
        },
        {
            question: 'What are your service fees?',
            answer: 'We charge a competitive commission only when you successfully rent your property. There are no upfront fees for listing your property with us.'
        },
        {
            question: 'How do you verify tenants?',
            answer: 'We conduct comprehensive background checks including credit history, employment verification, and reference checks to ensure reliable tenants.'
        },
        {
            question: 'Do you provide property management services?',
            answer: 'Yes, we offer full property management services including rent collection, maintenance coordination, and tenant communication.'
        }
    ];

    const [faqItems, setFaqItems] = useState(defaultFaqItems);

    useEffect(() => {
        metadataService.getFaqs()
            .then((faqs) => {
                if (faqs.length > 0) {
                    setFaqItems(faqs);
                }
            })
            .catch((error) => console.error('Error fetching FAQs:', error));
    }, []);

    return (
        <div className="min-h-screen bg-karibu-100">
            <SEO
                title="Contact Us"
                description="Get in touch with Rent Utopia. Reach our team by phone, email, or the contact form for help finding or listing a property in Kenya."
                canonicalPath="/contact"
            />
            {/* Hero Section */}
            <section className="relative bg-highland-700 text-karibu-100 py-20 overflow-hidden">
                <div
                    aria-hidden="true"
                    className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[420px] rounded-arch bg-gradient-to-b from-dawn-500/20 via-highland-500/10 to-transparent blur-2xl"
                />

                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center">
                        <h1 className="font-display text-4xl md:text-5xl font-bold mb-6">Get in Touch</h1>
                        <p className="text-xl text-karibu-200/80 max-w-3xl mx-auto">
                            Have questions about our services? Need help finding the perfect property?
                            We're here to help you every step of the way.
                        </p>
                    </div>
                </div>
            </section>

            {/* Contact Information Cards */}
            <section className="py-16 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {contactInfo.map((info, index) => {
                            const Icon = info.icon;
                            return (
                                <div key={index} className="text-center p-6 bg-soil-50 rounded-xl hover:shadow-lg transition-shadow duration-300">
                                    <div className="w-16 h-16 bg-highland-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
                                        <Icon className="w-8 h-8 text-white" />
                                    </div>
                                    <h3 className="font-display text-lg font-semibold text-soil-900 mb-3">{info.title}</h3>
                                    {info.details.map((detail, detailIndex) => (
                                        <p key={detailIndex} className="text-soil-600 font-medium mb-1">{detail}</p>
                                    ))}
                                    <p className="text-sm text-soil-600 mt-2">{info.description}</p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Contact Form and Map */}
            <section className="py-16 bg-soil-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                        {/* Contact Form */}
                        <div className="bg-white rounded-xl shadow-lg p-8">
                            <div className="mb-8">
                                <h2 className="font-display text-2xl font-display font-bold text-soil-900 mb-2">Send us a Message</h2>
                                <p className="text-soil-600">Fill out the details below and we'll get back to you as soon as possible.</p>
                            </div>

                            {submitStatus === 'success' && (
                                <div className="mb-6 p-4 bg-highland-50 border border-highland-100 rounded-lg flex items-center space-x-3">
                                    <CheckCircle className="w-5 h-5 text-highland-500" />
                                    <p className="text-highland-600">Thank you! Your message has been sent successfully.</p>
                                </div>
                            )}

                            {submitStatus === 'error' && (
                                <div className="mb-6 p-4 bg-murram-100/40 border border-murram-100 rounded-lg flex items-center space-x-3">
                                    <AlertCircle className="w-5 h-5 text-murram-500" />
                                    <p className="text-murram-600">Something went wrong. Please try again.</p>
                                </div>
                            )}

                            <div className="space-y-6">
                                {/* Name Fields */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-soil-600 mb-2">
                                            First Name *
                                        </label>
                                        <input
                                            type="text"
                                            name="firstName"
                                            value={formData.firstName}
                                            onChange={handleInputChange}
                                            required
                                            className="w-full px-4 py-3 border border-soil-100 rounded-lg focus:ring-2 focus:ring-highland-500 focus:border-transparent"
                                            placeholder="Enter your first name"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-soil-600 mb-2">
                                            Last Name *
                                        </label>
                                        <input
                                            type="text"
                                            name="lastName"
                                            value={formData.lastName}
                                            onChange={handleInputChange}
                                            required
                                            className="w-full px-4 py-3 border border-soil-100 rounded-lg focus:ring-2 focus:ring-highland-500 focus:border-transparent"
                                            placeholder="Enter your last name"
                                        />
                                    </div>
                                </div>

                                {/* Contact Fields */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-soil-600 mb-2">
                                            Email Address *
                                        </label>
                                        <input
                                            type="email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleInputChange}
                                            required
                                            className="w-full px-4 py-3 border border-soil-100 rounded-lg focus:ring-2 focus:ring-highland-500 focus:border-transparent"
                                            placeholder="your.email@example.com"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-soil-600 mb-2">
                                            Phone Number
                                        </label>
                                        <input
                                            type="tel"
                                            name="phone"
                                            value={formData.phone}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-3 border border-soil-100 rounded-lg focus:ring-2 focus:ring-highland-500 focus:border-transparent"
                                            placeholder="+254 700 123 456"
                                        />
                                    </div>
                                </div>

                                {/* Category and Subject */}
                                <div>
                                    <label className="block text-sm font-medium text-soil-600 mb-2">
                                        Category *
                                    </label>
                                    <select
                                        name="category"
                                        value={formData.category}
                                        onChange={handleInputChange}
                                        required
                                        className="w-full px-4 py-3 border border-soil-100 rounded-lg focus:ring-2 focus:ring-highland-500 focus:border-transparent"
                                    >
                                        <option value="">Select a category</option>
                                        <option value="general">General Inquiry</option>
                                        <option value="property-search">Property Search</option>
                                        <option value="property-listing">List a Property</option>
                                        <option value="technical">Technical Support</option>
                                        <option value="billing">Billing & Payments</option>
                                        <option value="partnership">Partnership</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-soil-600 mb-2">
                                        Subject *
                                    </label>
                                    <input
                                        type="text"
                                        name="subject"
                                        value={formData.subject}
                                        onChange={handleInputChange}
                                        required
                                        className="w-full px-4 py-3 border border-soil-100 rounded-lg focus:ring-2 focus:ring-highland-500 focus:border-transparent"
                                        placeholder="Brief description of your inquiry"
                                    />
                                </div>

                                {/* Message */}
                                <div>
                                    <label className="block text-sm font-medium text-soil-600 mb-2">
                                        Message *
                                    </label>
                                    <textarea
                                        name="message"
                                        value={formData.message}
                                        onChange={handleInputChange}
                                        required
                                        rows={5}
                                        className="w-full px-4 py-3 border border-soil-100 rounded-lg focus:ring-2 focus:ring-highland-500 focus:border-transparent resize-none"
                                        placeholder="Please provide details about your inquiry..."
                                    />
                                </div>

                                {/* Terms Agreement */}
                                <div className="flex items-start space-x-3">
                                    <input
                                        type="checkbox"
                                        name="agreeToTerms"
                                        checked={formData.agreeToTerms}
                                        onChange={handleInputChange}
                                        required
                                        className="mt-1 w-4 h-4 text-highland-500 border-soil-100 rounded focus:ring-highland-500"
                                    />
                                    <label className="text-sm text-soil-600">
                                        I agree to the <a href="/terms" className="text-highland-500 hover:underline">Terms of Service</a> and
                                        <a href="/privacy" className="text-highland-500 hover:underline ml-1">Privacy Policy</a>
                                    </label>
                                </div>

                                {/* Submit Button */}
                                <button
                                    onClick={handleSubmit}
                                    disabled={isSubmitting}
                                    className="w-full bg-highland-500 hover:bg-highland-600 disabled:bg-soil-400 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2"
                                >
                                    {isSubmitting ? (
                                        <>
                                            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                            <span>Sending...</span>
                                        </>
                                    ) : (
                                        <>
                                            <Send className="w-5 h-5" />
                                            <span>Send Message</span>
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>

                        {/* Map and Office Locations */}
                        <div className="space-y-8">
                            {/* Simulated Map */}
                            <div className="bg-gradient-to-br from-highland-50 to-karibu-200 rounded-xl h-80 flex items-center justify-center relative overflow-hidden">
                                <div className="absolute inset-0" style={{
                                    backgroundImage: `
                                        radial-gradient(circle at 20% 50%, rgba(31, 77, 61, 0.12), transparent 50%),
                                        radial-gradient(circle at 80% 20%, rgba(226, 162, 59, 0.15), transparent 50%),
                                        radial-gradient(circle at 40% 80%, rgba(124, 111, 166, 0.12), transparent 50%)
                                    `
                                }}></div>

                                <div className="relative z-10 text-center">
                                    <MapPin className="w-12 h-12 text-highland-500 mx-auto mb-4" />
                                    <h3 className="font-display text-lg font-semibold text-soil-900 mb-2">Find Us Here</h3>
                                    <p className="text-soil-600">Westlands Square, Nairobi</p>
                                </div>

                                {/* Map markers */}
                                <div className="absolute top-1/3 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                                    <div className="w-4 h-4 bg-murram-500 rounded-full border-2 border-white shadow-lg"></div>
                                </div>
                                <div className="absolute top-2/3 left-1/3 transform -translate-x-1/2 -translate-y-1/2">
                                    <div className="w-3 h-3 bg-dawn-500 rounded-full border-2 border-white shadow-lg"></div>
                                </div>
                                <div className="absolute top-1/2 right-1/3 transform translate-x-1/2 -translate-y-1/2">
                                    <div className="w-3 h-3 bg-highland-500 rounded-full border-2 border-white shadow-lg"></div>
                                </div>
                            </div>

                            {/* Office Locations */}
                            <div>
                                <h3 className="text-xl font-display font-bold text-soil-900 mb-6">Our Office Locations</h3>
                                <div className="space-y-4">
                                    {officeLocations.map((office, index) => (
                                        <div key={index} className="bg-white rounded-lg p-6 border border-soil-100 hover:shadow-md transition-shadow duration-200">
                                            <h4 className="font-semibold text-soil-900 mb-2">{office.name}</h4>
                                            <div className="space-y-2 text-sm text-soil-600">
                                                <div className="flex items-start space-x-2">
                                                    <MapPin className="w-4 h-4 mt-0.5 text-soil-400" />
                                                    <span>{office.address}</span>
                                                </div>
                                                <div className="flex items-center space-x-2">
                                                    <Phone className="w-4 h-4 text-soil-400" />
                                                    <span>{office.phone}</span>
                                                </div>
                                                <div className="flex items-center space-x-2">
                                                    <Mail className="w-4 h-4 text-soil-400" />
                                                    <span>{office.email}</span>
                                                </div>
                                                <div className="flex items-center space-x-2">
                                                    <Clock className="w-4 h-4 text-soil-400" />
                                                    <span>{office.hours}</span>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* FAQ Section */}
            <section className="py-16 bg-white">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl font-display font-bold text-soil-900 mb-4">Frequently Asked Questions</h2>
                        <p className="text-lg text-soil-600">Quick answers to common questions</p>
                    </div>

                    <div className="space-y-6">
                        {faqItems.map((faq, index) => (
                            <div key={index} className="bg-soil-50 rounded-lg p-6 hover:bg-soil-100 transition-colors duration-200">
                                <h3 className="font-semibold text-soil-900 mb-3 flex items-center">
                                    <MessageSquare className="w-5 h-5 mr-2 text-highland-500" />
                                    {faq.question}
                                </h3>
                                <p className="text-soil-600 ml-7">{faq.answer}</p>
                            </div>
                        ))}
                    </div>

                    <div className="text-center mt-12">
                        <p className="text-soil-600 mb-4">Can't find what you're looking for?</p>
                        <button className="bg-highland-500 hover:bg-highland-600 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200">
                            View All FAQs
                        </button>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Contact;